import React, { useEffect, useState, useRef } from "react";
import { Button, Form, Modal, Spinner, ButtonGroup, ToggleButton } from "react-bootstrap";
import QuizImageUpload from "./QuizImageUpload";

const AddQuizDialog = ({
  show,
  handleClose,
  handleQuizSubmit,
  editQuiz = false,
  quiz,
  updateQuizQuestion,
  activeQuiz,
  effectiveCourseId,
  activeSection,
  setAddQuestionBtnCLicked,
  onImageDelete, // callback to sync image deletions immediately to parent
}) => {
  const [questionDetails, setQuestionDetails] = useState({
    title: "",
    option1: "",
    option2: "",
    option3: "",
    option4: "",
    answer: "",
    explanation: "",
    point: "",
    isQuestionImage: false,
    questionImages: [],
    useOptionImages: false,
    option1Images: [],
    option2Images: [],
    option3Images: [],
    option4Images: [],
    showOption1Upload: false,
    showOption2Upload: false,
    showOption3Upload: false,
    showOption4Upload: false,
    rubric: [],
  });

  const [showExplanation, setShowExplanation] = useState(false);
  const [showMarks, setShowMarks] = useState(false);
  const [activeType, setActiveType] = useState(editQuiz ? quiz?.answerSelectionType || "single" : "single");

  const [errors, setErrors] = useState({
    title: "",
    options: "",
    answer: "",
    explanation: "",
    point: "",
    questionImages: "",
    optionImages: "",
    props: "",
    upload: "",
  });

  const [isDeletingImages, setIsDeletingImages] = useState(false); // Add this state for deletion loader

  const questionImageUploadRef = useRef(null);
  const option1ImageUploadRef = useRef(null);
  const option2ImageUploadRef = useRef(null);
  const option3ImageUploadRef = useRef(null);
  const option4ImageUploadRef = useRef(null);

  const canUseImages = effectiveCourseId && activeSection?.sectionId;

  useEffect(() => {
    if (!canUseImages && (questionDetails.isQuestionImage || questionDetails.useOptionImages)) {
      setQuestionDetails((prev) => ({
        ...prev,
        isQuestionImage: false,
        useOptionImages: false,
        questionImages: [],
        option1Images: [],
        option2Images: [],
        option3Images: [],
        option4Images: [],
      }));
      setErrors((prev) => ({
        ...prev,
        props: "Course ID and Section ID are required for image uploads",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        props: "",
      }));
    }
  }, [canUseImages, questionDetails.isQuestionImage, questionDetails.useOptionImages]);

  useEffect(() => {
    if (editQuiz && quiz) {
      setActiveType(quiz?.answerSelectionType || "single");
      const initialAnswer = quiz?.correctAnswer;
      let mappedAnswer = initialAnswer;
      
      // If correctAnswer is a string like "True" or "False", try to map it to index 1 or 2
      if (typeof initialAnswer === "string" && quiz?.answers) {
        const index = quiz.answers.findIndex(a => 
          a.answer === initialAnswer || a.value === initialAnswer
        );
        if (index !== -1) {
          mappedAnswer = index + 1;
        }
      }

      setQuestionDetails({
        title: quiz?.question || "",
        option1: quiz?.answers?.[0]?.answer || "",
        option2: quiz?.answers?.[1]?.answer || "",
        option3: quiz?.answers?.[2]?.answer || "",
        option4: quiz?.answers?.[3]?.answer || "",
        answer: mappedAnswer || "",
        explanation: quiz?.explanation || "",
        point: quiz?.point || "",
        isQuestionImage: quiz?.imageQuestion?.length > 0 || false,
        questionImages: quiz?.imageQuestion?.map((img) => ({ url: img, description: "" })) || [],
        useOptionImages: quiz?.answers?.some((ans) => ans.imageOptionName?.length > 0) || false,
        option1Images: quiz?.answers?.[0]?.imageOptionName?.map((img) => ({ url: img, description: "" })) || [],
        option2Images: quiz?.answers?.[1]?.imageOptionName?.map((img) => ({ url: img, description: "" })) || [],
        option3Images: quiz?.answers?.[2]?.imageOptionName?.map((img) => ({ url: img, description: "" })) || [],
        option4Images: quiz?.answers?.[3]?.imageOptionName?.map((img) => ({ url: img, description: "" })) || [],
        showOption1Upload: quiz?.answers?.[0]?.imageOptionName?.length > 0 || false,
        showOption2Upload: quiz?.answers?.[1]?.imageOptionName?.length > 0 || false,
        showOption3Upload: quiz?.answers?.[2]?.imageOptionName?.length > 0 || false,
        showOption4Upload: quiz?.answers?.[3]?.imageOptionName?.length > 0 || false,
        rubric: Array.isArray(quiz?.rubric) ? quiz.rubric : [],
      });
      setShowExplanation(!!quiz?.explanation);
      setShowMarks(!!quiz?.point);
    } else {
      setActiveType("single");
      setQuestionDetails({
        title: "",
        option1: "",
        option2: "",
        option3: "",
        option4: "",
        answer: "",
        explanation: "",
        point: "",
        isQuestionImage: false,
        questionImages: [],
        useOptionImages: false,
        option1Images: [],
        option2Images: [],
        option3Images: [],
        option4Images: [],
        showOption1Upload: false,
        showOption2Upload: false,
        showOption3Upload: false,
        showOption4Upload: false,
        rubric: [],
      });
      setErrors({
        title: "",
        options: "",
        answer: "",
        explanation: "",
        point: "",
        questionImages: "",
        optionImages: "",
        props: "",
        upload: "",
      });
      setShowExplanation(false);
      setShowMarks(false);
    }
  }, [editQuiz, quiz]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setQuestionDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = (name, imageData) => {
    if (!imageData || !Array.isArray(imageData)) {
      setErrors((prev) => ({
        ...prev,
        upload: `Failed to upload ${name.replace(/([A-Z])/g, ' $1').toLowerCase()}`,
      }));
      return;
    }
    setErrors((prev) => ({
      ...prev,
      upload: "",
    }));
    setQuestionDetails((prev) => ({
      ...prev,
      [name]: imageData,
    }));

    // Immediately sync image state to parent for deletion awareness
    if (editQuiz && onImageDelete) {
      const updatedImageNames = imageData
        .map(img => typeof img === "string" ? img : img.url?.split("/").pop())
        .filter(Boolean);
      if (name === "questionImages") {
        onImageDelete(quiz?.id, "question", null, updatedImageNames);
      } else {
        const optIndex = parseInt(name.replace("option", "").replace("Images", "")) - 1;
        onImageDelete(quiz?.id, "option", optIndex, updatedImageNames);
      }
    }
  };

  const generateOptionObject = (allOptions, allOptionImages) => {
    return allOptions
      ?.map((option, index) => ({
        answer: option,
        value: index + 1,
        imageOptionName: questionDetails.useOptionImages && allOptionImages[index].length > 0
          ? allOptionImages[index].map((img) =>
            typeof img === "string" ? img : img.url?.split("/").pop()
          )
          : [],
      }))
      .filter((opt) => opt.answer !== "");
  };

  const addRubricCriterion = () => {
    setQuestionDetails((prev) => ({
      ...prev,
      rubric: [
        ...(prev.rubric || []),
        { criterion: "", description: "", marks: 0, partial_credit_guidelines: "" },
      ],
    }));
  };

  const updateRubricField = (index, field, value) => {
    setQuestionDetails((prev) => ({
      ...prev,
      rubric: (prev.rubric || []).map((r, i) =>
        i === index ? { ...r, [field]: field === "marks" ? Number(value) : value } : r
      ),
    }));
  };

  const removeRubricCriterion = (index) => {
    setQuestionDetails((prev) => ({
      ...prev,
      rubric: (prev.rubric || []).filter((_, i) => i !== index),
    }));
  };

  // Images are completely optional - no validation needed

  const handleSubmit = async (isEditMode) => {
    setErrors({
      title: "",
      options: "",
      answer: "",
      questionImages: "",
      optionImages: "",
      props: "",
      upload: "",
    });

    if (questionDetails.title.length < 2) {
      setErrors((prev) => ({
        ...prev,
        title: "Question title is required",
      }));
      return;
    }

    if (activeType === "fill_in_the_blanks") {
      if (!/__+/.test(questionDetails.title)) {
        setErrors((prev) => ({
          ...prev,
          title: "Fill-in-the-blanks requires at least 2 consecutive underscores (__) for the blank.",
        }));
        return;
      }
      if (!questionDetails.answer) {
        setErrors((prev) => ({ ...prev, answer: "Answer cannot be empty" }));
        return;
      }
    } else if (activeType === "subjective") {
      if (!questionDetails.answer) {
        setErrors((prev) => ({ ...prev, answer: "Answer cannot be empty" }));
        return;
      }
    } else {
      const allOptions = [
        questionDetails.option1,
        questionDetails.option2,
        questionDetails.option3,
        questionDetails.option4,
      ].filter((option) => option !== "");
  
      if (allOptions.length < 2 && !questionDetails.useOptionImages) {
        setErrors((prev) => ({
          ...prev,
          options: "Minimum two options are required",
        }));
        return;
      }
  
      if (!questionDetails.answer) {
        setErrors((prev) => ({
          ...prev,
          answer: "Please select one answer",
        }));
        return;
      }
    }

    if (showExplanation && (!questionDetails.explanation || String(questionDetails.explanation).trim() === "")) {
      setErrors((prev) => ({ ...prev, explanation: "Explanation cannot be empty. Disable it if not needed." }));
      return;
    }
    
    if (showMarks && (!questionDetails.point || Number(questionDetails.point) <= 0)) {
      setErrors((prev) => ({ ...prev, point: "Marks must be an integer greater than 0." }));
      return;
    }

    // Handle image uploads
    let questionImages = questionDetails.questionImages;
    if (questionDetails.isQuestionImage && questionImageUploadRef.current) {
      const uploadedImages = await questionImageUploadRef.current.uploadImages();
      if (uploadedImages && Array.isArray(uploadedImages)) {
        questionImages = uploadedImages;
        setQuestionDetails((prev) => ({
          ...prev,
          questionImages,
        }));
      }
    }

    let optionImages = [
      questionDetails.option1Images,
      questionDetails.option2Images,
      questionDetails.option3Images,
      questionDetails.option4Images,
    ];
    if (questionDetails.useOptionImages) {
      const optionRefs = [option1ImageUploadRef, option2ImageUploadRef, option3ImageUploadRef, option4ImageUploadRef];
      const uploadedOptionImages = await Promise.all(
        [1, 2, 3, 4].map(async (item, index) => {
          if (questionDetails[`showOption${item}Upload`] && optionRefs[index].current) {
            const result = await optionRefs[index].current.uploadImages();
            return Array.isArray(result) ? result : questionDetails[`option${item}Images`];
          }
          return questionDetails[`option${item}Images`];
        })
      );
      optionImages = uploadedOptionImages;
      setQuestionDetails((prev) => ({
        ...prev,
        option1Images: uploadedOptionImages[0],
        option2Images: uploadedOptionImages[1],
        option3Images: uploadedOptionImages[2],
        option4Images: uploadedOptionImages[3],
      }));
    }

    // Process queued deletions in edit mode
    if (isEditMode && questionDetails.isQuestionImage && questionImageUploadRef.current) {
      setIsDeletingImages(true);
      await questionImageUploadRef.current.processDeletions();
    }
    if (isEditMode && questionDetails.useOptionImages) {
      const optionRefs = [option1ImageUploadRef, option2ImageUploadRef, option3ImageUploadRef, option4ImageUploadRef];
      setIsDeletingImages(true);
      await Promise.all(
        [1, 2, 3, 4].map(async (item, index) => {
          if (questionDetails[`showOption${item}Upload`] && optionRefs[index].current) {
            await optionRefs[index].current.processDeletions();
          }
        })
      );
    }
    setIsDeletingImages(false);

    const quizDetails = {
      id: isEditMode ? quiz.id : (activeQuiz?.questions?.length || 0) + 1,
      question: questionDetails.title,
      imageQuestion: questionDetails.isQuestionImage
        ? questionImages.map((img) =>
          typeof img === "string" ? img : img.url?.split("/").pop()
        )
        : [],
      answers: activeType === "single" ? generateOptionObject(
        [
          questionDetails.option1,
          questionDetails.option2,
          questionDetails.option3,
          questionDetails.option4,
        ],
        optionImages
      ) : [],
      correctAnswer: activeType === "single" ? Number(questionDetails.answer) : questionDetails.answer,
      answerSelectionType: activeType,
      interface: activeType === "single" ? "radio" : activeType === "fill_in_the_blanks" ? "text" : "textarea",
    };
    
    if (showExplanation) quizDetails.explanation = questionDetails.explanation;
    if (showMarks) quizDetails.point = Number(questionDetails.point);

    // updateQuizQuestion fully replaces the question, so always send the
    // current rubric state (edited or carried over from the original question).
    if (Array.isArray(questionDetails.rubric) && questionDetails.rubric.length > 0) {
      quizDetails.rubric = questionDetails.rubric;
    }

    if (isEditMode) {
      updateQuizQuestion(quizDetails);
    } else {
      handleQuizSubmit(quizDetails);
    }
    setAddQuestionBtnCLicked(false);
    handleClose();
  };

  const handleDialogClose = () => {
    // On close, sync any image deletions that happened (images are already deleted from S3)
    if (editQuiz && onImageDelete && quiz) {
      const currentQImages = questionDetails.questionImages
        .map(img => typeof img === "string" ? img : img.url?.split("/").pop())
        .filter(Boolean);
      onImageDelete(quiz.id, "question", null, currentQImages);

      if (activeType === "single") {
        [1, 2, 3, 4].forEach((item, idx) => {
          const currentOptImages = questionDetails[`option${item}Images`]
            .map(img => typeof img === "string" ? img : img.url?.split("/").pop())
            .filter(Boolean);
          onImageDelete(quiz.id, "option", idx, currentOptImages);
        });
      }
    }

    setActiveType("single");
    setQuestionDetails({
      title: "",
      option1: "",
      option2: "",
      option3: "",
      option4: "",
      answer: "",
      explanation: "",
      point: "",
      isQuestionImage: false,
      questionImages: [],
      useOptionImages: false,
      option1Images: [],
      option2Images: [],
      option3Images: [],
      option4Images: [],
      showOption1Upload: false,
      showOption2Upload: false,
      showOption3Upload: false,
      showOption4Upload: false,
      rubric: [],
    });
    setErrors({
      title: "",
      options: "",
      answer: "",
      questionImages: "",
      optionImages: "",
      props: "",
      upload: "",
    });
    setAddQuestionBtnCLicked(false);
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleDialogClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>{editQuiz ? "Edit Quiz Question" : "Add Quiz Question"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="mb-4 d-flex justify-content-center border-bottom pb-4">
            <ButtonGroup>
              <ToggleButton
                key="single"
                id="radio-single"
                type="radio"
                variant={activeType === "single" ? "primary" : "outline-primary"}
                name="quizType"
                value="single"
                checked={activeType === "single"}
                onChange={(e) => {
                  setActiveType(e.currentTarget.value);
                  setQuestionDetails(prev => ({...prev, answer: ""}));
                }}
              >
                Multiple Choice (MCQ)
              </ToggleButton>
              <ToggleButton
                key="fill_in_the_blanks"
                id="radio-fib"
                type="radio"
                variant={activeType === "fill_in_the_blanks" ? "primary" : "outline-primary"}
                name="quizType"
                value="fill_in_the_blanks"
                checked={activeType === "fill_in_the_blanks"}
                onChange={(e) => {
                  setActiveType(e.currentTarget.value);
                  setQuestionDetails(prev => ({...prev, answer: ""}));
                }}
              >
                Fill in the Blanks (FIB)
              </ToggleButton>
              <ToggleButton
                key="subjective"
                id="radio-subjective"
                type="radio"
                variant={activeType === "subjective" ? "primary" : "outline-primary"}
                name="quizType"
                value="subjective"
                checked={activeType === "subjective"}
                onChange={(e) => {
                  setActiveType(e.currentTarget.value);
                  setQuestionDetails(prev => ({...prev, answer: ""}));
                }}
              >
                Subjective
              </ToggleButton>
            </ButtonGroup>
          </div>
          <div className="mb-5">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="quiz-title" className="d-flex align-items-center gap-2">
                <span>{activeType === "fill_in_the_blanks" ? "Question (Include at least 2 or more underscores '__' for the blank)" : "Question"}</span>
                {canUseImages && (
                  <i
                    className={`bi bi-image cursor-pointer ${questionDetails.isQuestionImage ? 'text-primary' : 'text-muted'}`}
                    style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                    title={questionDetails.isQuestionImage ? 'Hide image upload' : 'Add image to question'}
                    onClick={(e) => {
                      e.preventDefault();
                      setQuestionDetails(prev => ({
                        ...prev,
                        isQuestionImage: !prev.isQuestionImage,
                      }));
                    }}
                  />
                )}
              </Form.Label>
              <Form.Control
                placeholder="Write quiz question"
                id="quiz-title"
                name="title"
                value={questionDetails.title}
                onChange={handleChange}
                className="text-dark"
              />
              <div className="text-danger">{errors.title}</div>
            </Form.Group>
            {questionDetails.isQuestionImage && canUseImages && (
              <Form.Group className="mb-3">
                <Form.Label>Question Images (Select multiple images)</Form.Label>
                <QuizImageUpload
                  ref={questionImageUploadRef}
                  onImageUpload={(images) => handleImageUpload("questionImages", images)}
                  existingImages={questionDetails.questionImages}
                  effectiveCourseId={effectiveCourseId}
                  activeSection={activeSection}
                  editQuiz={editQuiz}
                />
                <div className="text-danger">{errors.questionImages}</div>
              </Form.Group>
            )}
          </div>
          <div>
            <h4 className="mb-3 border-bottom p-2">Add Quiz Options/Answer</h4>
            <Form.Group className="mb-3">
              {/* Image checkboxes... */}
            </Form.Group>
            
            {activeType === "single" ? (
              [1, 2, 3, 4].map((item, index) => (
                <Form.Group className="mb-2" key={index}>
                  <Form.Label
                    className="mb-2 d-flex justify-content-between align-items-center fw-normal"
                    htmlFor={`input-choice-${item}`}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0 fw-normal">Answer {item}</h5>
                      {canUseImages && (
                        <i
                          className={`bi bi-image cursor-pointer ${questionDetails[`option${item}Images`]?.length > 0 || questionDetails[`showOption${item}Upload`] ? 'text-primary' : 'text-muted'}`}
                          style={{ cursor: 'pointer', fontSize: '0.95rem' }}
                          title={questionDetails[`showOption${item}Upload`] ? `Hide image upload for option ${item}` : `Add image to option ${item}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setQuestionDetails(prev => ({
                              ...prev,
                              [`showOption${item}Upload`]: !prev[`showOption${item}Upload`],
                              useOptionImages: true,
                            }));
                          }}
                        />
                      )}
                    </div>
                    <div className="d-flex align-items-center lh-1">
                      <span>Correct answer</span>
                      <Form.Check
                        type="switch"
                        id={`custom-switch-${item}`}
                        className="ms-2"
                        name="answer"
                        onChange={(e) => {
                          setQuestionDetails((prev) => ({
                            ...prev,
                            answer: index + 1,
                          }));
                        }}
                        checked={
                          questionDetails.answer === (index + 1) || 
                          String(questionDetails.answer) === String(index + 1) ||
                          String(questionDetails.answer).toLowerCase() === String(questionDetails[`option${index + 1}`]).toLowerCase()
                        }
                      />
                    </div>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id={`input-choice-${item}`}
                    name={`option${item}`}
                    placeholder={questionDetails[`showOption${item}Upload`] ? "Write the answer (optional if using images)" : "Write the answer"}
                    onChange={handleChange}
                    value={questionDetails[`option${item}`]}
                    className="text-dark mb-2"
                  />
                  {questionDetails[`showOption${item}Upload`] && canUseImages && (
                    <QuizImageUpload
                      ref={[option1ImageUploadRef, option2ImageUploadRef, option3ImageUploadRef, option4ImageUploadRef][index]}
                      onImageUpload={(images) => handleImageUpload(`option${item}Images`, images)}
                      existingImages={questionDetails[`option${item}Images`]}
                      effectiveCourseId={effectiveCourseId}
                      activeSection={activeSection}
                      editQuiz={editQuiz}
                    />
                  )}
                  {errors.optionImages && (
                    <div className="text-danger">{errors.optionImages}</div>
                  )}
                </Form.Group>
              ))
            ) : activeType === "fill_in_the_blanks" ? (
               <Form.Group className="mb-3">
                 <Form.Label className="fw-normal">Correct Answer (Fill in the blank)</Form.Label>
                 <Form.Control
                   type="text"
                   name="answer"
                   value={questionDetails.answer}
                   onChange={handleChange}
                   placeholder="Enter the correct term"
                   className="text-dark"
                 />
               </Form.Group>
            ) : (
               <Form.Group className="mb-3">
                 <Form.Label className="fw-normal">Correct Answer (Subjective Reference)</Form.Label>
                 <Form.Control
                   as="textarea"
                   rows={4}
                   name="answer"
                   value={questionDetails.answer}
                   onChange={handleChange}
                   placeholder="Enter the expected complete answer for grading reference"
                   className="text-dark"
                 />
               </Form.Group>
            )}
            
            <div className="text-danger">{errors.options}</div>
            <div className="text-danger">{errors.answer}</div>
            
            <div className="mt-4 pt-3 border-top">
              <h5 className="mb-3">Optional Details</h5>
              
              {!showExplanation ? (
                <Button variant="outline-primary" size="sm" className="mb-3 me-2" onClick={() => setShowExplanation(true)}>
                  + Add Explanation
                </Button>
              ) : (
                <Form.Group className="mb-3 position-relative">
                  <div className="d-flex justify-content-between">
                    <Form.Label className="fw-normal">Explanation</Form.Label>
                    <span 
                       className="text-danger cursor-pointer small" 
                       style={{cursor: 'pointer'}} 
                       onClick={() => { setShowExplanation(false); setQuestionDetails(prev => ({...prev, explanation: ""}))}}
                    >
                      Remove
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="explanation"
                    value={questionDetails.explanation}
                    onChange={handleChange}
                    placeholder="Provide an explanation for the correct answer"
                    className="text-dark"
                  />
                  <div className="text-danger small mt-1">{errors.explanation}</div>
                </Form.Group>
              )}

              {!showMarks ? (
                <Button variant="outline-primary" size="sm" className="mb-3" onClick={() => setShowMarks(true)}>
                  + Add Marks
                </Button>
              ) : (
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between">
                    <Form.Label className="fw-normal">Marks (Points)</Form.Label>
                     <span 
                       className="text-danger cursor-pointer small" 
                       style={{cursor: 'pointer'}} 
                       onClick={() => { setShowMarks(false); setQuestionDetails(prev => ({...prev, point: ""}))}}
                    >
                      Remove
                    </span>
                  </div>
                  <Form.Control
                    type="number"
                    name="point"
                    value={questionDetails.point}
                    onChange={handleChange}
                    placeholder="e.g. 5"
                    className="text-dark"
                    min="1"
                  />
                  <div className="text-danger small mt-1">{errors.point}</div>
                </Form.Group>
              )}

              {activeType === "subjective" && (
                <div className="mt-4 pt-3 border-top">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Rubric</h5>
                    <Button variant="outline-primary" size="sm" onClick={addRubricCriterion}>
                      + Add Criterion
                    </Button>
                  </div>

                  {(!questionDetails.rubric || questionDetails.rubric.length === 0) && (
                    <div className="text-muted small mb-2">No rubric criteria yet.</div>
                  )}

                  {(questionDetails.rubric || []).map((r, idx) => (
                    <div key={idx} className="border rounded-3 p-3 mb-2 bg-light">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1">
                          <Form.Group className="mb-2">
                            <Form.Label className="fw-normal small">Criterion</Form.Label>
                            <Form.Control
                              type="text"
                              value={r.criterion || ""}
                              onChange={(e) => updateRubricField(idx, "criterion", e.target.value)}
                              placeholder="Criterion name"
                              className="text-dark"
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="fw-normal small">Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={r.description || ""}
                              onChange={(e) => updateRubricField(idx, "description", e.target.value)}
                              placeholder="Describe what this criterion evaluates"
                              className="text-dark"
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="fw-normal small">Marks</Form.Label>
                            <Form.Control
                              type="number"
                              value={r.marks ?? 0}
                              onChange={(e) => updateRubricField(idx, "marks", e.target.value)}
                              placeholder="Marks"
                              className="text-dark"
                              style={{ maxWidth: "120px" }}
                            />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label className="fw-normal small">Partial Credit Guidelines</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={r.partial_credit_guidelines || ""}
                              onChange={(e) => updateRubricField(idx, "partial_credit_guidelines", e.target.value)}
                              placeholder="Explain how partial credit should be awarded"
                              className="text-dark"
                            />
                          </Form.Group>
                        </div>
                        <Button variant="outline-danger" size="sm" onClick={() => removeRubricCriterion(idx)}>
                          &times;
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-danger">{errors.props}</div>
            <div className="text-danger">{errors.upload}</div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="secondary" onClick={handleDialogClose}>
          Close
        </Button>
        {editQuiz ? (
          <Button
            variant="primary"
            onClick={() => handleSubmit(true)}
            disabled={isDeletingImages} // Disable button during deletion
          >
            {isDeletingImages ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />

              </>
            ) : (
              "Edit Quiz"
            )}
          </Button>
        ) : (
          <Button variant="primary" onClick={() => handleSubmit(false)}>
            +
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddQuizDialog;